import { DesignData } from 'editor/src/store/design/types';
import { RootState } from 'editor/src/store/index';

import { getDesignKeyFromDesign } from './getDesignKey';

// Reflecting onto a template with other spreads or pages would drop or blank some of them.
const hasSamePageLayout = (designData: DesignData, template: DesignData): boolean =>
  template.spreads.length === designData.spreads.length &&
  designData.spreads.every((spread, spreadIndex) => spread.pages.length === template.spreads[spreadIndex].pages.length);

/**
 * The template the active page-count design must be laid back onto, when its page sizes no longer
 * match the template for its own page count.
 *
 * The cover of a page-count product grows with the spine, and the editor leaves that geometry to the
 * template the host sends. A design saved while its cover and page count disagreed keeps the wrong
 * cover until something re-lays it out, and preflight rejects that cover, so its mockups render blank.
 */
const getDriftedPageCountTemplate = (state: RootState): DesignData | undefined => {
  const { designData } = state.design;
  if (!designData?.page_count_limit) {
    return undefined;
  }

  const template = state.variants.designTemplates[getDesignKeyFromDesign(designData)];
  if (!template || !hasSamePageLayout(designData, template)) {
    return undefined;
  }

  const pageSizesDiffer = designData.spreads.some((spread, spreadIndex) =>
    spread.pages.some(
      (page, pageIndex) =>
        page.width !== template.spreads[spreadIndex].pages[pageIndex].width ||
        page.height !== template.spreads[spreadIndex].pages[pageIndex].height,
    ),
  );

  return pageSizesDiffer ? template : undefined;
};

export default getDriftedPageCountTemplate;
